import React from 'react'
import { msgid, ngettext, t } from 'ttag'
import {
  CustomType,
  DivisionCategoryRow,
  GoalWithProgress,
  Lang,
  ProgressType,
  UserCompanyDto,
} from '../../../../services/api.types'
import { CustomTypeCategory, customTypes, customUnits, getCustomTypeTextById } from '../../../../services/CustomTypes'
import { getPeriodString } from '../../../../common/helpers/date'
import { formatThousands } from '../../../../common/helpers/translations'
import {
  getAvgSuffix,
  GoalType,
  getDivisionCategoryIdFromLeaderboardId,
  isActivityIndexType,
  isDivisionCategoryLeaderboardId,
} from '../../../../common/helpers/challenges'
import {
  CHALLENGE_ADMIN_STEP_ACTIVITY_TYPE,
  CHALLENGE_ADMIN_STEP_CHALLENGE_TYPE,
  CHALLENGE_ADMIN_STEP_CONFIGURATION,
  CHALLENGE_ADMIN_STEP_DETAILS,
  CHALLENGE_ADMIN_STEP_GOAL,
  CHALLENGE_ADMIN_STEP_LEADERBOARDS,
  CHALLENGE_ADMIN_STEP_ORGANIZER,
  CHALLENGE_ADMIN_STEP_PERIOD,
  CHALLENGE_ADMIN_STEP_POINTS,
  CHALLENGE_ADMIN_STEP_TOTAL_POINTS,
  consistencyUnit,
  dailyGoalUnit,
  FormData,
} from '../../ChallengeAdmin'
import './ChallengeReview.scss'
import YumField from '../../../../components/YumInput/YumInput'
import EditIcon from '@material-ui/icons/Edit'
import InputAdornment from '@material-ui/core/InputAdornment'
import { IconButton, Step, StepContent, StepLabel, Stepper } from '@material-ui/core'
import { gray, primaryBlue } from '../../../../styles/material-ui-theme-v2'
import { PointsFormula } from '../../../ChallengeInfo/ChallengeInfo'
import { currentLang } from '../../../../i18nInit'
import { hasCompanyFeatureFlag } from '../../../../common/helpers/featureFlags'
import { formatDescriptionMarkdown } from '../../../../common/helpers/activitiesHelpers'
import { translateDivisionCategoryType } from '../../../TeamManagement/TeamManagement'
import { ActivityView } from '../../../Activity/store/ActivityReducer'
import { isDailyMaxEnabled } from '../ChallengeStepGoal/goalValidationHelpers'

export interface IChallengeReviewProps {
  editMode: boolean
  data: FormData
  formStep: number
  setFormStep: any
  userCompanies: UserCompanyDto[]
  goals: GoalWithProgress[]
  divisionCategories: DivisionCategoryRow[]
  isLegacyChallenge: boolean
}

export default function ChallengeReview(props: IChallengeReviewProps) {
  const { editMode, data, formStep, setFormStep, userCompanies, goals, divisionCategories, isLegacyChallenge } = props
  const {
    maxFormStep,

    // Step 0.
    companyId,
    companyName,
    newCommunityName,

    // Step 1.
    activityType,
    customActivityName,

    // Step 2.
    leaderboards,
    //asCollectiveUnit,
    chatEnabled,
    manualEntryEnabled,
    roundDailyPoints,
    autoJoinEnabled,
    maxManualEntryDays,

    challengePoints,
    challengeTotalPoints,

    // Step 3.
    dateRange,
    days,

    // Step 4.
    goalType,
    totalGoal,
    dailyMax,
    collectiveGoal,
    persistenceGoal,
    dailyGoal,
    customUnit,
    customType,
    indexTarget,
    simpleManualEntry,
    showGroupMembers,
    showGroupMembersContributions,
    hideLeaderboardTables,
    avgAgg,

    // Step 5.
    title,
    prize,
    description,
  } = data

  const useMarkdown = hasCompanyFeatureFlag(userCompanies, companyId as string, 'challengeDescriptionMarkdown')

  const setStep = (step: number) => () => {
    setFormStep(step)
  }

  const Field = ({ step, ariaLabel: ariaLabelProp, ...props }: any) => {
    // Fall back to the visible label so every Edit button gets a unique name.
    const ariaLabel = ariaLabelProp || props.label
    return (
      <YumField
        variant={props.label ? 'filled' : 'outlined'}
        disabled
        InputProps={{
          endAdornment: step !== false && (
            <InputAdornment position={'end'} style={{ marginTop: props.multiline ? '-6px' : 0 }}>
              <IconButton
                edge="end"
                onClick={setStep(step)}
                title={ariaLabel ? t`Edit ${ariaLabel}` : t`Edit`}
                aria-label={ariaLabel ? t`Edit ${ariaLabel}` : t`Edit`}
              >
                <EditIcon style={{ color: primaryBlue }} aria-hidden="true" />
              </IconButton>
            </InputAdornment>
          ),
        }}
        // Provide an accessible name when no visible label is rendered, so the
        // disabled review input still satisfies the axe `label` rule (WCAG 1.3.1).
        inputProps={!props.label && ariaLabel ? { 'aria-label': ariaLabel } : undefined}
        style={{
          marginBottom: 0,
        }}
        {...props}
      />
    )
  }

  const isPopularCustomActivity = customTypes.some((ct) => ct.id === activityType)
  const customTypeUnit = customUnits.find((cu) => cu.id === customType)?.text

  const company = companyId === 'new' ? newCommunityName /*+ ' (' + t`new community` + ')'*/ : companyName
  const organizerStep = companyId && (
    <Field step={editMode ? false : CHALLENGE_ADMIN_STEP_ORGANIZER} ariaLabel={t`Organizer`} value={company} />
  )

  const activityTypeStep = activityType && (
    <Field
      step={CHALLENGE_ADMIN_STEP_ACTIVITY_TYPE}
      ariaLabel={t`Activity type`}
      value={
        activityType === ProgressType.CUSTOM
          ? t`Custom` + ': ' + customActivityName + '\n' + t`Unit` + ': ' + customUnit
          : getCustomTypeTextById(activityType)
      }
      multiline
    />
  )

  // Points configuration step - only shown for POINTS activity type
  const pointsStep = [CustomTypeCategory.POINTS].includes(activityType as CustomTypeCategory) && challengePoints && (
    <span
      style={{
        //marginTop: '10px',
        padding: '10px',
        borderRadius: '10px',
        backgroundColor: gray,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <PointsFormula
        pointsLogic={challengePoints}
        roundDailyPoints={roundDailyPoints}
        isReview={true}
        style={{ fontSize: '0.8125rem' }}
      />
      <InputAdornment position={'end'} style={{ marginRight: 5 }}>
        <IconButton
          edge="end"
          onClick={setStep(CHALLENGE_ADMIN_STEP_POINTS)}
          title={t`Edit points`}
          aria-label={t`Edit points`}
        >
          <EditIcon style={{ color: primaryBlue }} aria-hidden="true" />
        </IconButton>
      </InputAdornment>
    </span>
  )

  // Total points challenges step - only shown for TOTAL_POINTS activity type
  // Display the list of selected challenges
  const totalPointsChallenges =
    challengeTotalPoints && challengeTotalPoints.length > 0
      ? challengeTotalPoints
          .map((item: any) => {
            const challenge = goals.find((g) => g.id === item.id)
            return challenge ? challenge.title : null
          })
          .filter((title: string | null) => title !== null)
          .join('\n')
      : null

  const totalPointsStep = [CustomTypeCategory.TOTAL_POINTS].includes(activityType as CustomTypeCategory) &&
    totalPointsChallenges && (
      <Field
        step={CHALLENGE_ADMIN_STEP_TOTAL_POINTS}
        ariaLabel={t`Wellness points challenges`}
        value={totalPointsChallenges}
        multiline
      />
      // <span
      //   style={{
      //     marginTop: '10px',
      //     padding: '10px',
      //     borderRadius: '10px',
      //     backgroundColor: gray,
      //     display: 'flex',
      //     alignItems: 'center',
      //     justifyContent: 'space-between',
      //   }}
      // >
      //   <span /*style={{ fontSize: '0.8125rem' }}*/>{totalPointsChallenges}</span>
      //   <InputAdornment position={'end'} style={{ marginRight: 5 }}>
      //     <IconButton edge="end" onClick={setStep(CHALLENGE_ADMIN_STEP_TOTAL_POINTS)}>
      //       <EditIcon style={{ color: primaryBlue }} />
      //     </IconButton>
      //   </InputAdornment>
      // </span>
    )

  const showAutoJoinOption = hasCompanyFeatureFlag(userCompanies, companyId as string, 'challengeAutoJoinOption')

  // Leaderboards configuration - follow the saved order from the form.
  let orderedLeaderboards = leaderboards || []
  const leaderboardsSummary: string[] = []

  // If legacy collective goal, add a collective goal
  const isLegacyCollectiveGoal = goalType === 'collective'
  if (isLegacyCollectiveGoal && isLegacyChallenge && !orderedLeaderboards.includes(ActivityView.COLLECTIVE)) {
    orderedLeaderboards = [...orderedLeaderboards, ActivityView.COLLECTIVE]
  }

  orderedLeaderboards.forEach((leaderboardId) => {
    if (leaderboardId === ActivityView.INDIVIDUAL) {
      leaderboardsSummary.push(t`Individuals`)
      return
    }
    if (leaderboardId === ActivityView.GROUP) {
      leaderboardsSummary.push(t`Teams`)
      return
    }
    if (leaderboardId === ActivityView.COUNTRY) {
      leaderboardsSummary.push(t`Countries`)
      return
    }
    if (leaderboardId === ActivityView.COLLECTIVE) {
      leaderboardsSummary.push(t`Collective`)
      return
    }

    // Division category leaderboards are stored with a prefix.
    if (isDivisionCategoryLeaderboardId(leaderboardId)) {
      const categoryId = getDivisionCategoryIdFromLeaderboardId(leaderboardId)
      const category = divisionCategories.find((item) => item.id === categoryId)
      if (category) {
        leaderboardsSummary.push(translateDivisionCategoryType(category, true))
      }
    }
  })

  const leaderboardsStep = leaderboardsSummary.length && (
    <>
      <Field
        step={CHALLENGE_ADMIN_STEP_LEADERBOARDS}
        ariaLabel={t`Leaderboards`}
        value={leaderboardsSummary.join('\n')}
        multiline
      />
    </>
  )

  // Challenge type step - displays the selected goal type
  const challengeTypeLabel =
    goalType === 'collective' ? t`Collective target` : goalType === 'totalAmount' ? t`Total target` : t`Daily target`
  const challengeTypeStep = goalType && (
    <Field step={CHALLENGE_ADMIN_STEP_CHALLENGE_TYPE} ariaLabel={t`Challenge type`} value={challengeTypeLabel} />
  )

  // Other configuration options
  const configuration = []
  //if (asCollectiveUnit) configuration.push(t`Collective goal`)
  if (chatEnabled) configuration.push(t`Chat`)
  if (autoJoinEnabled && showAutoJoinOption) configuration.push(t`Auto-join users`)
  if (showGroupMembers) configuration.push(t`Show group participants`)
  if (showGroupMembersContributions) configuration.push(t`Show group participants contributions`)
  if (hideLeaderboardTables) configuration.push(t`Hide leaderboard tables`)
  if (roundDailyPoints && customType === CustomType.POINTS) configuration.push(t`Rounding of daily points`)
  if (manualEntryEnabled && customType !== CustomType.TOTAL_POINTS)
    configuration.push(
      t`Manual entry` +
        (maxManualEntryDays ? ` (${maxManualEntryDays} ${ngettext(msgid`day`, `days`, maxManualEntryDays)})` : ''),
    )
  if (simpleManualEntry && manualEntryEnabled && customType !== CustomType.TOTAL_POINTS)
    configuration.push(t`One-click manual entry`)

  const dailyMaxSupported = isDailyMaxEnabled(
    CHALLENGE_ADMIN_STEP_CONFIGURATION,
    activityType as CustomTypeCategory,
    goalType,
    simpleManualEntry,
  )

  // Determine which unit to use based on activity type
  const goalCustomTypeUnit = customUnits.find((cu) => cu.id === customType)?.text
  const unitText =
    activityType === CustomTypeCategory.CUSTOM
      ? customUnit
      : isPopularCustomActivity
        ? goalCustomTypeUnit
        : dailyGoalUnit(activityType as ProgressType, customType, customUnit)
  // Check if daily max is enabled for this goal
  // const isPoints = goalCustomType === CustomType.TOTAL_POINTS || customType === CustomType.POINTS
  // const dailyMaxEnabled =
  //   goalDailyMax &&
  //   goalDailyMax > 0 &&
  //   activityType !== CustomTypeCategory.ACTIVITY_INDEX &&
  //   ['totalAmount', 'collective'].includes(goalType) &&
  //   !isPoints

  const configurationStep = configuration.length && (
    <>
      <Field
        step={CHALLENGE_ADMIN_STEP_CONFIGURATION}
        ariaLabel={t`Configuration`}
        value={configuration.join('\n')}
        multiline
      />
      {/* Show daily max if enabled */}
      {dailyMaxSupported && dailyMax && Number(dailyMax) > 0 ? (
        <Field
          key="dailyMax"
          step={CHALLENGE_ADMIN_STEP_CONFIGURATION}
          label={t`Daily limit per user`}
          value={`${formatThousands(dailyMax)} ${unitText}`}
          style={{
            marginTop: '10px',
          }}
        />
      ) : null}
    </>
  )

  const periodString =
    dateRange && dateRange[0] && dateRange[1]
      ? getPeriodString(dateRange[0].getTime(), dateRange[1].getTime(), true)
      : ''
  const daysText = days ? ngettext(msgid`day`, `days`, days) : ''
  const period = `${periodString} (${days} ${daysText})`
  const datePeriodStep = dateRange && <Field step={CHALLENGE_ADMIN_STEP_PERIOD} ariaLabel={t`Period`} value={period} />

  // Helper function to get goal value from nested path
  // Handles both form-level fields and goal-specific fields
  const getGoalValue = (path: string): any => {
    const parts = path.split('.')
    let value: any = data
    for (const part of parts) {
      if (value && typeof value === 'object') {
        value = value[part]
      } else {
        return undefined
      }
    }
    return value
  }

  // Helper function to render a single goal section
  // Renders the appropriate fields based on goalType and activityType
  // Handles null/undefined goalConfig and checks if goal should be shown based on enabled state and values
  const renderGoalSection = (
    title: string,
    goalConfig: any,
    goalPath: string, // e.g., 'goals.individual', 'goals.team', etc.
  ) => {
    // Extract goal type from goalPath (e.g., 'goals.team' → 'team', 'goals.group.abc123' → 'group')
    const goalPathType = goalPath.split('.')[1] as GoalType

    // Determine if this goal uses form-level fields (when fieldPrefix is empty)
    const usesFormLevelFields = false // goalPath === 'goals.individual' //|| goalPath === 'goals.collective'

    // For individual/collective goals, check if there are form-level values even if goalConfig is null
    if (usesFormLevelFields) {
      const hasFormLevelValues =
        /*typeof totalGoal === 'number' &&*/ (totalGoal && totalGoal > 0) ||
        /*typeof dailyGoal === 'number' &&*/ (dailyGoal && dailyGoal > 0) ||
        /*typeof persistenceGoal === 'number' &&*/ (persistenceGoal && persistenceGoal > 0) ||
        /*typeof collectiveGoal === 'number' &&*/ (collectiveGoal && collectiveGoal > 0) ||
        (activityType === CustomTypeCategory.ALL &&
          /*typeof indexTarget === 'number' &&*/ indexTarget &&
          indexTarget > 0)

      // Create default config if needed
      if (!goalConfig && hasFormLevelValues) {
        goalConfig = { enabled: false, totalGoal: '' }
      }

      // For collective goals, also check if collectiveGoal value exists
      // if (goalPath === 'goals.collective' && !(typeof collectiveGoal === 'number' && collectiveGoal > 0)) {
      //   if (!goalConfig || !goalConfig.enabled) {
      //     return null
      //   }
      // }
    }

    // Only render if goal config exists and is enabled
    if (!goalConfig || !goalConfig.enabled) {
      return null
    }

    // Get goal-specific values
    // For individual/collective goals (usesFormLevelFields = true):
    //   - Use form-level values directly (totalGoal, dailyMax, customType, customUnit) since fieldPrefix is empty
    //   - GoalConfig.totalGoal is just metadata, form-level values are the source
    // For team/country/group goals (usesFormLevelFields = false):
    //   - Check goalConfig values first (goals.team.totalGoal, etc.), then fallback to form-level
    // Note: dailyMax, customType, and customUnit are always at form level, not in GoalConfig
    const goalTotalGoal = usesFormLevelFields
      ? totalGoal
      : goalConfig.totalGoal && goalConfig.totalGoal !== ''
        ? goalConfig.totalGoal
        : ''
    // dailyMax, customType, and customUnit are always taken from form level, not from goalConfig
    const goalDailyMax = dailyMax
    // These fields are always at form level for individual/collective, or goal-specific for others
    const goalDailyGoal = usesFormLevelFields ? dailyGoal : (getGoalValue(`${goalPath}.dailyGoal`) ?? dailyGoal)
    const goalPersistenceGoal = usesFormLevelFields
      ? persistenceGoal
      : (getGoalValue(`${goalPath}.persistenceGoal`) ?? persistenceGoal)
    const goalCollectiveGoal = usesFormLevelFields
      ? collectiveGoal
      : (getGoalValue(`${goalPath}.collectiveGoal`) ?? collectiveGoal)
    const goalIndexTarget = usesFormLevelFields ? indexTarget : (getGoalValue(`${goalPath}.indexTarget`) ?? indexTarget)
    const goalSimpleManualEntry = usesFormLevelFields
      ? simpleManualEntry
      : (getGoalValue(`${goalPath}.simpleManualEntry`) ?? simpleManualEntry)
    // customType and customUnit are always at form level, not in GoalConfig
    const goalCustomType = customType
    const goalCustomUnit = customUnit

    // // Determine which unit to use based on activity type
    // const goalCustomTypeUnit = customUnits.find((cu) => cu.id === goalCustomType)?.text
    // const unitText =
    //   activityType === CustomTypeCategory.CUSTOM
    //     ? goalCustomUnit
    //     : isPopularCustomActivity
    //       ? goalCustomTypeUnit
    //       : dailyGoalUnit(activityType as ProgressType, goalCustomType, goalCustomUnit)
    // // Check if daily max is enabled for this goal
    // const isPoints = goalCustomType === CustomType.TOTAL_POINTS || goalCustomType === CustomType.POINTS
    // const dailyMaxEnabled =
    //   goalDailyMax &&
    //   goalDailyMax > 0 &&
    //   activityType !== CustomTypeCategory.ACTIVITY_INDEX &&
    //   ['totalAmount', 'collective'].includes(goalType) &&
    //   !isPoints

    const goalFields = []

    // For activity index (ALL activity type), show target
    if (goalType === 'totalAmount' && activityType === CustomTypeCategory.ALL && goalIndexTarget) {
      goalFields.push(
        <Field
          key="indexTarget"
          step={CHALLENGE_ADMIN_STEP_GOAL}
          label={t`Target`}
          value={`${goalIndexTarget}%`}
          style={{
            marginBottom: '10px',
            // marginBottom: goalType === 'consistentDays' && goalPersistenceGoal ? '10px' : 0,
          }}
        />,
      )
    }

    // For collective goals - check if this is the collective goal section
    // The goalConfig.enabled is already checked at the beginning of the function
    // const isCollectiveGoalSection = goalPath === 'goals.collective'
    // // Check if goalCollectiveGoal is a number and greater than 0
    // const hasCollectiveGoal = /*typeof goalCollectiveGoal === 'number' &&*/ goalCollectiveGoal > 0

    // if (isCollectiveGoalSection && hasCollectiveGoal) {
    //   goalFields.push(
    //     <Field
    //       key="collectiveGoal"
    //       step={CHALLENGE_ADMIN_STEP_GOAL}
    //       label={t`Collective goal`}
    //       value={`${formatThousands(goalCollectiveGoal)} ${unitText}`}
    //     />,
    //   )

    //   // if (dailyMaxEnabled) {
    //   //   goalFields.push(
    //   //     <Field
    //   //       key="dailyMax"
    //   //       step={CHALLENGE_ADMIN_STEP_GOAL}
    //   //       label={t`Daily limit per user`}
    //   //       value={`${formatThousands(goalDailyMax)} ${unitText}`}
    //   //       style={{
    //   //         marginTop: '10px',
    //   //       }}
    //   //     />,
    //   //   )
    //   // }
    // } else {
    // For total amount and consistency goals
    // Show total goal for totalAmount goals (not consistency)
    // Check if goalTotalGoal is a number and greater than 0
    const hasTotalGoal = /*typeof goalTotalGoal === 'number' &&*/ goalTotalGoal > 0
    if (
      !['consistentDays', 'consistentDaysBinary'].includes(goalType) &&
      hasTotalGoal &&
      !isActivityIndexType(activityType as ProgressType)
    ) {
      // For group-based goals (team, country, group), show AVG suffix if avgAgg is enabled
      const avgSuffix = getAvgSuffix(goalPathType, avgAgg)

      // Helper function to get the goal unit text
      // Custom activities use goalCustomUnit, otherwise use dailyGoalUnit
      const getGoalUnitText =
        activityType === CustomTypeCategory.CUSTOM ? goalCustomUnit : dailyGoalUnit(activityType, goalCustomType)

      goalFields.push(
        <Field
          key="totalGoal"
          step={CHALLENGE_ADMIN_STEP_GOAL}
          label={t`Total goal`}
          value={`${formatThousands(goalTotalGoal)} ${getGoalUnitText}${avgSuffix}`}
        />,
      )
    }

    // Show consistency goal for consistency goal types
    // Check if goalPersistenceGoal is a number and greater than 0
    const hasPersistenceGoal = /*typeof goalPersistenceGoal === 'number' &&*/ goalPersistenceGoal > 0
    if (['consistentDays', 'consistentDaysBinary'].includes(goalType) && hasPersistenceGoal) {
      goalFields.push(
        <Field
          key="persistenceGoal"
          step={CHALLENGE_ADMIN_STEP_GOAL}
          label={t`Total goal`}
          value={`${formatThousands(goalPersistenceGoal)} ${goalPathType === 'individual' ? consistencyUnit(days) : ngettext(msgid`day`, `days`, days ?? 2)}`}
        />,
      )
    }

    // Don't render if no fields to show
    if (goalFields.length === 0) {
      return null
    }

    return (
      <div key={goalPath} style={{ marginBottom: '10px' }}>
        <div style={{ marginBottom: '8px' /*, fontWeight: 500 */ }}>{title}</div>
        {goalFields}
      </div>
    )
  }

  // Render all enabled goals in the exact order defined by leaderboards.
  const goalsContent = []

  // Individual goals (only if not calories activity type)
  const isCalories = activityType === CustomTypeCategory.CALORIES
  const showNonIndividualGoals = !isLegacyChallenge // !isActivityIndexType(activityType as ProgressType)

  if (orderedLeaderboards.length > 0) {
    orderedLeaderboards.forEach((leaderboardId) => {
      if (leaderboardId === ActivityView.INDIVIDUAL) {
        if (isCalories) return
        const individualGoal = renderGoalSection(t`Individuals`, data.goals?.individual, 'goals.individual')
        if (individualGoal) goalsContent.push(individualGoal)
        return
      }

      if (leaderboardId === ActivityView.GROUP) {
        if (!showNonIndividualGoals || isCalories || !data.goals?.team) return
        const teamGoal = renderGoalSection(t`Teams`, data.goals.team, 'goals.team')
        if (teamGoal) goalsContent.push(teamGoal)
        return
      }

      if (leaderboardId === ActivityView.COUNTRY) {
        if (!showNonIndividualGoals || isCalories || !data.goals?.country) return
        const countryGoal = renderGoalSection(t`Countries`, data.goals.country, 'goals.country')
        if (countryGoal) goalsContent.push(countryGoal)
        return
      }

      if (leaderboardId === ActivityView.COLLECTIVE) {
        if (!showNonIndividualGoals || activityType === CustomTypeCategory.ALL) return
        const collectiveGoalSection = renderGoalSection(t`Collective`, data.goals?.collective, 'goals.collective')
        if (collectiveGoalSection) goalsContent.push(collectiveGoalSection)
        return
      }

      if (isDivisionCategoryLeaderboardId(leaderboardId)) {
        if (!showNonIndividualGoals || isCalories || !data.goals?.group || !divisionCategories) return
        const categoryId = getDivisionCategoryIdFromLeaderboardId(leaderboardId)
        if (!categoryId) return
        const groupGoal = data.goals.group[categoryId]
        if (!groupGoal) return
        const categoryMatch = divisionCategories.find((category) => category.id === categoryId)
        if (!categoryMatch) return
        const categoryName = translateDivisionCategoryType(categoryMatch)
        const renderedGoal = renderGoalSection(categoryName, groupGoal, `goals.group.${categoryId}`)
        if (renderedGoal) goalsContent.push(renderedGoal)
      }
    })
  } else {
    if (!isCalories) {
      const individualGoal = renderGoalSection(t`Individuals`, data.goals?.individual, 'goals.individual')
      if (individualGoal) goalsContent.push(individualGoal)
    }

    if (showNonIndividualGoals) {
      if (!isCalories && data.goals?.team) {
        const teamGoal = renderGoalSection(t`Teams`, data.goals.team, 'goals.team')
        if (teamGoal) goalsContent.push(teamGoal)
      }

      if (!isCalories && data.goals?.group && divisionCategories) {
        divisionCategories.forEach((category) => {
          const groupGoal = data.goals.group[category.id]
          if (!groupGoal) return
          const categoryName = translateDivisionCategoryType(category)
          const renderedGoal = renderGoalSection(categoryName, groupGoal, `goals.group.${category.id}`)
          if (renderedGoal) goalsContent.push(renderedGoal)
        })
      }

      if (!isCalories && data.goals?.country) {
        const countryGoal = renderGoalSection(t`Countries`, data.goals.country, 'goals.country')
        if (countryGoal) goalsContent.push(countryGoal)
      }

      if (activityType !== CustomTypeCategory.ALL) {
        const collectiveGoalSection = renderGoalSection(t`Collective`, data.goals?.collective, 'goals.collective')
        if (collectiveGoalSection) goalsContent.push(collectiveGoalSection)
      }
    }
  }

  // Show daily goal before goal sections (outside goalSection)
  // Check if dailyGoal is a number and greater than 0
  const dailyGoalValue = typeof dailyGoal === 'number' ? dailyGoal : Number(dailyGoal)
  const hasDailyGoal = Number.isFinite(dailyGoalValue) && dailyGoalValue > 0
  const shouldShowDailyGoal =
    goalsContent.length > 0 &&
    ((goalType === 'totalAmount' && activityType !== CustomTypeCategory.CUSTOM && !isPopularCustomActivity) ||
      //(goalType === 'consistentDays' && !simpleManualEntry)) &&
      goalType === 'consistentDays') &&
    hasDailyGoal
  const spacer = isActivityIndexType(activityType as ProgressType) ? '' : ' '
  const dailyGoalField = shouldShowDailyGoal ? (
    <Field
      step={CHALLENGE_ADMIN_STEP_GOAL}
      label={t`Daily goal`}
      value={`${formatThousands(dailyGoalValue)}${spacer}${dailyGoalUnit(activityType as ProgressType, customType, customUnit)}`}
      style={{
        marginBottom: '10px',
      }}
    />
  ) : null

  const detailsStep = (
    <>
      {title && (
        <Field
          step={CHALLENGE_ADMIN_STEP_DETAILS}
          label={t`Challenge name`}
          value={title}
          style={{
            marginBottom: prize || description ? '10px' : 0,
          }}
        />
      )}
      {prize && (
        <Field
          step={CHALLENGE_ADMIN_STEP_DETAILS}
          label={t`Prize`}
          value={prize}
          style={{
            marginBottom: description ? '10px' : 0,
          }}
          multiline
        />
      )}
      {description &&
        (useMarkdown ? (
          <div
            style={{
              position: 'relative',
              backgroundColor: gray,
              borderRadius: '10px',
              padding: '22px 10px 10px',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: '6px',
                left: '10px',
                fontSize: '10.4px',
                color: 'rgba(0, 0, 0, 0.38)',
              }}
            >
              {t`Description`}
            </div>
            <div className="challenge-review-markdown">{formatDescriptionMarkdown(description)}</div>
            <div style={{ position: 'absolute', top: '2px', right: '13px' }}>
              <IconButton
                edge="end"
                onClick={setStep(CHALLENGE_ADMIN_STEP_DETAILS)}
                title={t`Edit description`}
                aria-label={t`Edit description`}
              >
                <EditIcon style={{ color: primaryBlue }} aria-hidden="true" />
              </IconButton>
            </div>
          </div>
        ) : (
          <Field
            step={CHALLENGE_ADMIN_STEP_DETAILS}
            label={t`Description`}
            value={description}
            multiline
            className="challenge-review-description"
          />
        ))}
    </>
  )

  // Build steps array conditionally - include Points step for POINTS, Total Points step for TOTAL_POINTS
  const steps = [
    { label: t`Organizer`, content: organizerStep },
    { label: t`Activity type`, content: activityTypeStep },
    // Only show Points step for POINTS activity type
    ...([CustomTypeCategory.POINTS].includes(activityType as CustomTypeCategory)
      ? [{ label: t`Points logic`, content: pointsStep }]
      : []),
    { label: t`Period`, content: datePeriodStep },
    // Only show Total Points step for TOTAL_POINTS activity type
    ...([CustomTypeCategory.TOTAL_POINTS].includes(activityType as CustomTypeCategory)
      ? [{ label: t`Wellness points challenges`, content: totalPointsStep }]
      : []),
    { label: t`Leaderboards`, content: leaderboardsStep },
    { label: t`Challenge type`, content: challengeTypeStep },
    {
      label: t`Goals`,
      // Show a clear placeholder when no goals are configured.
      content:
        goalsContent.length > 0 ? (
          <>
            {dailyGoalField}
            {goalsContent}
          </>
        ) : (
          <Field value={t`No goals`} ariaLabel={t`Goals`} step={CHALLENGE_ADMIN_STEP_GOAL} />
        ),
    },
    { label: t`Configuration`, content: configurationStep },
    { label: t`Details`, content: detailsStep },
  ]

  // Map formStep to the correct index in the review steps array
  // Steps are conditionally shown based on activity type
  const getActiveStepIndex = () => {
    let index = formStep

    // If Points step is not shown, subtract 1 from formStep if it's >= POINTS step (2)
    if (
      ![CustomTypeCategory.POINTS].includes(activityType as CustomTypeCategory) &&
      formStep >= CHALLENGE_ADMIN_STEP_POINTS
    ) {
      index--
    }

    // If Total Points step is not shown, subtract 1 from formStep if it's >= TOTAL_POINTS step (4)
    if (
      ![CustomTypeCategory.TOTAL_POINTS].includes(activityType as CustomTypeCategory) &&
      formStep >= CHALLENGE_ADMIN_STEP_TOTAL_POINTS
    ) {
      index--
    }

    return index
  }

  // Adjust maxFormStep similarly - subtract for steps that are not shown
  const getAdjustedMaxFormStep = () => {
    let adjusted = maxFormStep

    // If Points step is not shown, reduce by 1 if maxFormStep is >= POINTS step
    if (
      ![CustomTypeCategory.POINTS].includes(activityType as CustomTypeCategory) &&
      maxFormStep >= CHALLENGE_ADMIN_STEP_POINTS
    ) {
      adjusted--
    }

    // If Total Points step is not shown, reduce by 1 if maxFormStep is >= TOTAL_POINTS step
    if (
      ![CustomTypeCategory.TOTAL_POINTS].includes(activityType as CustomTypeCategory) &&
      maxFormStep >= CHALLENGE_ADMIN_STEP_TOTAL_POINTS
    ) {
      adjusted--
    }

    return adjusted
  }

  const activeStepIndex = getActiveStepIndex()
  const adjustedMaxFormStep = getAdjustedMaxFormStep()

  return (
    <div className="challenge-review">
      <Stepper activeStep={activeStepIndex} orientation="vertical">
        {steps.map(({ label, content }, index) => (
          <Step key={`step-${index}`} expanded={index < adjustedMaxFormStep} disabled={false}>
            {/* Expose each step label as a heading (under the "Challenge overview" h2) so screen
                reader users can navigate the overview sections by heading, not just read them inline. */}
            <StepLabel>
              <span role="heading" aria-level={3}>
                {label}
              </span>
            </StepLabel>
            {index < adjustedMaxFormStep ? <StepContent>{content}</StepContent> : ''}
          </Step>
        ))}
      </Stepper>
    </div>
  )
}
